import {
  Box,
  BoxProps,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ComponentProps, useCallback, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatNumber, isDefined } from "@app/utils";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { addYears, format, formatISO } from "date-fns";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarIconZoomIn, CarIconZoomOut } from "@app/components/Icons";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { RegressionChartData } from "@app/api/carApi.generated";
import { CarChartLegend } from "./ChartLegend";

export interface IndexCompareItem {
  name: string;
  regressionChartData?: RegressionChartData;
  priceDrivenExpectedReturn?: number;
  volatility?: number;
  expectedTrendReturn?: number;
}

type Options = ComponentProps<typeof Chart>["options"];

export interface IndexCompareDataPoint {
  x: string;
  y: number;
}

interface IndexCompareData {
  datasets: ChartDataset<"line", IndexCompareDataPoint[]>[];
  xLabel: string;
  yLabel: string;
}

export const getIndexCompareData = ({
  theme,
  item1,
  item2,
  item3,
}: {
  theme: Theme;
  item1?: IndexCompareItem;
  item2?: IndexCompareItem;
  item3?: IndexCompareItem;
}): IndexCompareData => {
  const getIndexCompareDataset = ({
    item,
    index,
    color,
  }: {
    item?: IndexCompareItem;
    index: number;
    color: string;
  }): IndexCompareData["datasets"] => {
    return item
      ? [
          {
            type: "line",
            label: "Trend Line",
            id: `trend_line${index}`,
            data:
              item.regressionChartData?.trendline?.x?.map((i, idx) => ({
                x: i.toString() ?? "",
                y: item.regressionChartData?.trendline?.y?.[idx] ?? 0,
              })) ?? [],
            borderColor: color,
            borderDash: [4, 4],
            pointBorderColor: color,
            pointBackgroundColor: color,
            pointBorderWidth: 0,
            pointRadius: 0,
            borderWidth: 1,
          },
          {
            type: "line",
            label: "Historic Line",
            id: `historic_line${index}`,
            data:
              item.regressionChartData?.data_points?.map((i, idx) => ({
                x: formatISO(new Date((i.x ?? 0) / 1000 / 1000), {
                  representation: "date",
                }),
                y: i.y ?? 0,
              })) ?? [],
            borderColor: color,
            pointBorderColor: color,
            pointBackgroundColor: color,
            pointBorderWidth: 0,
            pointRadius: 0,
            borderWidth: 1,
          },
        ]
      : [];
  };

  const result: IndexCompareData = {
    datasets: [
      ...getIndexCompareDataset({
        item: item1,
        index: 1,
        color: theme.palette.chartsColor.blue,
      }),
      ...getIndexCompareDataset({
        item: item2,
        index: 2,
        color: theme.palette.chartsColor.orange,
      }),
      ...getIndexCompareDataset({
        item: item3,
        index: 3,
        color: theme.palette.chartsColor.green,
      }),
    ],
    xLabel: `${[item1, item2, item3]
      .filter(isDefined)
      .map((i) => i.name)
      .join(" vs. ")} Value`,
    yLabel: "Value Added Monthly Index",
  };
  return result;
};

interface ChartIndexCompareProps {
  sx?: BoxProps["sx"];
  item1?: IndexCompareItem;
  item2?: IndexCompareItem;
  item3?: IndexCompareItem;
  isWhiteContext: boolean;
}

export const ChartIndexCompare = ({
  sx,
  item1,
  item2,
  item3,
  isWhiteContext,
}: ChartIndexCompareProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabel } = useMemo(
    () =>
      getIndexCompareData({
        theme,
        item1,
        item2,
        item3,
      }),
    [theme, item1, item2, item3],
  );

  const yFormatter = (value: string | number) =>
    typeof value === "number"
      ? formatNumber(value, { decimalPlaces: 2, forceShowDecimals: true })
      : value;

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "ChartTotalReturns",
  });

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    IndexCompareDataPoint[],
    unknown
  > | null>();

  const renderTooltip: RenderTooltip = useCallback(
    (datasetIndex, dataIndex) => {
      const ds = datasets.at(datasetIndex);
      const value = ds?.data?.at(dataIndex)?.y ?? 0;
      const dateStr = ds?.data?.at(dataIndex)?.x;
      const date = dateStr ? format(new Date(dateStr), "yyyy MMM") : undefined;
      return (
        <>
          <Box
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: "black",
              whiteSpace: "nowrap",
            }}
          >
            <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
              {ds?.label}
            </Typography>
            {Boolean(date) && (
              <>
                <br />
                <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
                  {date}
                </Typography>
              </>
            )}
            <br />
            <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
              {yFormatter(value)}
            </Typography>
          </Box>
        </>
      );
    },
    [datasets],
  );

  const options = useMemo<Options>(
    () => ({
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      scales: {
        x: {
          // offset: true,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
          },
          grid: {
            display: false,
          },
          type: "time",
          time: {
            unit: "year",
          },
        },
        y: {
          offset: false,
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },

          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: yFormatter,
          },
          grid: {
            display: true,
            color: (ctx) => {
              return ctx.tick.value === 0
                ? theme.palette.gray7
                : theme.palette.gray2;
            },
            lineWidth: 1,
            tickColor: theme.palette.white,
          },
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        tooltip: tooltipPlugin,
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          limits: {
            x: {
              min: "original",
              max: "original",
              minRange: 5 * 365 * 24 * 60 * 60 * 1000, // max zoom level - 5 years
            },
          },
          zoom: {
            pinch: {
              enabled: true,
            },
            mode: "x",
          },
        },
        periods: {
          backgroundColor: "rgba(236, 238, 240, 0.5)",
          borderColor: "rgba(178, 179, 181, 0.5)",
          font: {
            family: theme.typography.fontFamily,
            style: "italic",
            size: 12,
          },
          textColor: theme.palette.black,
          labelPosition: "horizontal",
          items: [
            {
              start: addYears(new Date(), -10).getTime(),
              end: new Date().getTime(),
              label: "Timeframe too short for 10 year analysis",
            },
          ],
        },
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
      },
    }),
    [theme, tooltipPlugin, yLabel, isWhiteContext],
  );

  const getChartLegendItem = (item?: IndexCompareItem, color?: string) =>
    item ? (
      <CarChartLegend
        sx={{ flexDirection: "column" }}
        items={[
          {
            label: `${item.name} Historic Line`,
            color: color ?? "",
          },
          {
            label: `${item.name} Trend Line`,
            color: color ?? "",
            isDottedLine: true,
          },
        ]}
      />
    ) : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", ...sx }}>
      <Box sx={{ display: "flex", zIndex: 1, alignItems: "flex-end" }}>
        <Typography
          variant="par03SemiBold"
          sx={{ fontSize: "19px", mb: 0.5, ml: 8 }}
        >
          {xLabel}
          <CarTooltipBox
            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
            className={pendoClasses.simulationModelsIndexCompareAssetClasses}
          />
        </Typography>
        <IconButton
          sx={{ ml: "auto" }}
          size="small"
          onClick={() => {
            chartRef.current?.zoom(1.5, "zoom");
          }}
        >
          <CarIconZoomIn fontSize="large" color="primary" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            chartRef.current?.zoom(-1.5, "zoom");
          }}
        >
          <CarIconZoomOut fontSize="large" color="primary" />
        </IconButton>
      </Box>
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          mt: -1,
          position: "relative",
        }}
      >
        <CarTooltipBox
          sx={{ position: "absolute", left: 5, top: 72 }}
          className={pendoClasses.simulationModelsIndexCompareAddedMonthlyIndex}
        />
        <Chart
          height={420}
          ref={chartRef}
          onDoubleClick={() => {
            chartRef.current?.resetZoom();
          }}
          type="line"
          datasetIdKey="id"
          data={{
            datasets,
          }}
          options={options}
          plugins={[ChartPlugins.roundedBackground, ChartPlugins.periods]}
        />
      </ChartTooltip>
      <Box
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
        }}
      >
        {getChartLegendItem(item1, theme.palette.chartsColor.blue)}
        {getChartLegendItem(item2, theme.palette.chartsColor.orange)}
        {getChartLegendItem(item3, theme.palette.chartsColor.green)}
      </Box>
    </Box>
  );
};
